
import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-10 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="https://public.readdy.ai/ai/img_res/522fb4cf-c33f-44b8-ab6d-29781757781b.png" alt="MARIZO d. o. o." className="h-10 w-10 object-contain" />
              <h1 className="text-lg font-medium text-gray-900">MARIZO d. o. o.</h1>
            </div>
            <div className="flex items-center gap-16">
              <button onClick={() => scrollToSection('home')} className="text-base text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-base text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('products')} className="text-base text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Products</button>
              <button onClick={() => scrollToSection('contact')} className="text-base text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-40 pb-32 px-10">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center gap-16">
            <div className="w-[45%]">
              <h2 className="text-[72px] font-bold leading-[1.1] mb-12">
                <span className="text-gray-900">Modern iOS</span><br />
                <span className="bg-gradient-to-r from-[#0066ff] to-[#0052cc] bg-clip-text text-transparent">Applications</span>
              </h2>
              <p className="text-2xl text-gray-600 leading-relaxed max-w-[520px]">
                We develop cutting-edge VPN and Cleaner solutions that prioritize user privacy and device performance
              </p>
            </div>
            <div className="w-[55%] flex justify-end">
              <div className="relative w-full h-[600px]">
                <img 
                  src="https://public.readdy.ai/ai/img_res/55d0cf4dfc25e48dac1414516a738ffb.jpg" 
                  alt="iOS Applications" 
                  className="w-full h-full object-cover rounded-3xl shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-10 bg-[#fafafa]">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-24">
            <p className="text-xs uppercase tracking-[2px] text-gray-500 mb-8 ml-20">WHO WE ARE</p>
            <div className="ml-[65%]">
              <h3 className="text-[56px] font-bold leading-[1.3] mb-12 text-gray-900">
                Expert Team Building<br />Secure Mobile<br />Solutions
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed max-w-[480px]">
                MARIZO d. o. o. is a dedicated team of iOS development experts specializing in creating innovative applications that enhance user experience and strengthen mobile device security. Our flagship products, including advanced VPN protection and intelligent device cleaning tools, are crafted with meticulous attention to detail and user-centric design principles.
              </p>
            </div>
          </div>
          <div className="flex gap-8">
            <div className="w-[38%]">
              <div className="w-full h-[400px]">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20software%20development%20team%20collaborating%20in%20modern%20minimalist%20office%20workspace%2C%20developers%20working%20on%20iOS%20applications%20with%20MacBooks%20and%20large%20monitors%2C%20clean%20white%20and%20light%20gray%20interior%20design%2C%20natural%20lighting%2C%20contemporary%20tech%20company%20environment%2C%20professional%20business%20photography%2C%20high%20quality%20corporate%20imagery&width=600&height=400&seq=about-team-collab&orientation=landscape" 
                  alt="Team Collaboration" 
                  className="w-full h-full object-cover rounded-[20px] shadow-lg hover:scale-[1.02] transition-transform duration-300"
                />
              </div>
            </div>
            <div className="w-[58%]">
              <div className="w-full h-[400px]">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20software%20development%20workspace%20with%20multiple%20monitors%20displaying%20iOS%20app%20code%20and%20design%20interfaces%2C%20clean%20minimalist%20desk%20setup%20with%20MacBook%20Pro%2C%20iPhone%20devices%2C%20wireless%20keyboard%20and%20mouse%2C%20bright%20white%20workspace%20with%20plants%2C%20professional%20tech%20environment%2C%20ultra%20clean%20aesthetic%2C%20high-end%20workspace%20photography&width=900&height=400&seq=about-workspace&orientation=landscape" 
                  alt="Development Workspace" 
                  className="w-full h-full object-cover rounded-[20px] shadow-lg hover:scale-[1.02] transition-transform duration-300"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 px-10">
        <div className="max-w-[1400px] mx-auto">
          <h3 className="text-[64px] font-bold text-center mb-20">
            Our <span className="bg-gradient-to-r from-[#0066ff] to-[#8b5cf6] bg-clip-text text-transparent">iOS Products</span>
          </h3>
          <div className="flex gap-6" data-product-shop>
            <div className="w-[50%]">
              <div className="h-[600px] rounded-[28px] bg-gradient-to-br from-[#1e3a8a] to-[#6b21a8] relative overflow-hidden shadow-xl group cursor-pointer">
                <img 
                  src="https://readdy.ai/api/search-image?query=Premium%20VPN%20security%20shield%20icon%20with%20lock%20symbol%20and%20encrypted%20data%20streams%2C%20glowing%20blue%20and%20purple%20neon%20lights%2C%20digital%20security%20visualization%2C%20abstract%20cybersecurity%20concept%20art%2C%20floating%20geometric%20shapes%20representing%20network%20protection%2C%20dark%20navy%20blue%20gradient%20background%2C%20modern%20tech%20illustration%2C%20high-tech%20security%20interface%20design&width=600&height=600&seq=product-vpn&orientation=squarish" 
                  alt="VPN Protection" 
                  className="w-full h-full object-cover opacity-60 group-hover:opacity-70 transition-opacity duration-300"
                />
                <div className="absolute bottom-8 left-8 bg-black/60 backdrop-blur-sm px-6 py-4 rounded-xl">
                  <h4 className="text-white text-2xl font-semibold">VPN Protection</h4>
                </div>
              </div>
            </div>
            <div className="w-[48%] flex flex-col gap-6">
              <div className="h-[280px] rounded-[28px] bg-[#e8f5f0] relative overflow-hidden shadow-xl group cursor-pointer">
                <img 
                  src="https://readdy.ai/api/search-image?query=Minimalist%20device%20cleaning%20tool%20icon%20with%20sparkles%20and%20broom%20symbol%2C%20fresh%20mint%20green%20background%2C%20clean%20and%20organized%20mobile%20phone%20interface%2C%20storage%20optimization%20visualization%2C%20simple%20geometric%20shapes%2C%20modern%20app%20icon%20design%2C%20soft%20pastel%20colors%2C%20professional%20product%20illustration%2C%20ultra%20clean%20aesthetic&width=700&height=280&seq=product-cleaner&orientation=landscape" 
                  alt="Device Cleaner" 
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-90 transition-opacity duration-300"
                />
                <div className="absolute bottom-8 left-8 bg-black/60 backdrop-blur-sm px-6 py-4 rounded-xl">
                  <h4 className="text-white text-2xl font-semibold">Device Cleaner</h4>
                </div>
              </div>
              <div className="h-[288px] rounded-[28px] bg-[#ffe5e5] relative overflow-hidden shadow-xl group cursor-pointer">
                <img 
                  src="https://readdy.ai/api/search-image?query=Performance%20metrics%20dashboard%20with%20speed%20gauges%20and%20optimization%20charts%2C%20warm%20coral%20pink%20background%2C%20mobile%20device%20performance%20indicators%2C%20battery%20and%20memory%20usage%20graphs%2C%20modern%20UI%20design%20elements%2C%20clean%20data%20visualization%2C%20professional%20app%20interface%2C%20soft%20pastel%20aesthetic%2C%20high-quality%20digital%20illustration&width=700&height=288&seq=product-performance&orientation=landscape" 
                  alt="Performance Tools" 
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-90 transition-opacity duration-300"
                />
                <div className="absolute bottom-8 left-8 bg-black/60 backdrop-blur-sm px-6 py-4 rounded-xl">
                  <h4 className="text-white text-2xl font-semibold">Performance Tools</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-32 px-10 bg-[#fafafa]">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex gap-16">
            <div className="w-[42%]">
              <div className="w-full h-[500px] rounded-[32px] overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20business%20person%20portrait%20on%20solid%20sky%20blue%20background%2C%20confident%20tech%20professional%20in%20modern%20casual%20business%20attire%2C%20clean%20studio%20photography%2C%20bright%20and%20friendly%20expression%2C%20high-quality%20corporate%20headshot%2C%20minimalist%20background%2C%20professional%20lighting%2C%20contemporary%20business%20portrait%20style&width=500&height=500&seq=testimonial-client&orientation=squarish" 
                  alt="Client Testimonial" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="w-[58%] pl-16 flex flex-col justify-center">
              <p className="text-sm text-gray-500 mb-20">(Client Feedback)</p>
              <h4 className="text-5xl font-bold mb-4 text-gray-900">Exceptional Quality and</h4>
              <h4 className="text-5xl font-bold mb-16 text-gray-400">Performance</h4>
              <div className="mb-8">
                <p className="text-xl text-gray-600 leading-relaxed max-w-[520px]">
                  <span className="text-3xl text-gray-300">"</span>The VPN application provides outstanding security features while maintaining excellent connection speeds. The Cleaner tool significantly improved my device performance.<span className="text-3xl text-gray-300">"</span>
                </p>
              </div>
              <p className="text-xl font-bold text-gray-900">— Sarah Mitchell</p>
              <div className="flex gap-4 mt-12">
                <button className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center hover:-translate-y-1 transition-transform duration-300 cursor-pointer">
                  <i className="ri-arrow-left-line text-gray-600 text-xl"></i>
                </button>
                <button className="w-12 h-12 rounded-full bg-black flex items-center justify-center hover:-translate-y-1 transition-transform duration-300 cursor-pointer">
                  <i className="ri-arrow-right-line text-white text-xl"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Footer Section */}
      <section id="contact" className="py-24 px-10 border-t border-gray-200">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex gap-20">
            <div className="w-[40%]">
              <h4 className="text-[42px] font-bold text-gray-900 mb-8">Get in Touch</h4>
              <p className="text-base text-gray-600 mb-14">
                We're here to answer your questions about our iOS applications and services
              </p>
              <div>
                <p className="text-sm uppercase tracking-wider text-gray-500 mb-4">Stay Updated</p>
                <input 
                  type="email" 
                  placeholder="Enter your email" 
                  className="w-full bg-transparent border-b-2 border-gray-300 pb-3 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-900 transition-colors mb-6"
                />
                <button className="border-2 border-black bg-transparent px-12 py-3 rounded-full text-base font-medium text-gray-900 hover:bg-black hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap">
                  Subscribe
                </button>
              </div>
            </div>
            <div className="w-[60%] flex gap-12">
              <div className="flex-1">
                <h5 className="text-sm uppercase tracking-wider text-gray-400 mb-6">Contact</h5>
                <div className="space-y-5">
                  <div>
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-2">
                      <i className="ri-mail-line text-lg"></i>
                      <span>Email</span>
                    </p>
                    <a href="mailto:contact@marizodoo.com" className="text-base text-gray-900 hover:text-[#0066ff] transition-colors cursor-pointer">
                      contact@marizodoo.com
                    </a>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-2">
                      <i className="ri-phone-line text-lg"></i>
                      <span>Phone</span>
                    </p>
                    <a href="tel:+16088512173" className="text-base text-gray-900 hover:text-[#0066ff] transition-colors cursor-pointer">
                      +1 6088512173
                    </a>
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <h5 className="text-sm uppercase tracking-wider text-gray-400 mb-6">Location</h5>
                <div className="space-y-4">
                  <p className="text-base text-gray-900 flex items-center gap-2">
                    <i className="ri-map-pin-line text-lg"></i>
                    <span>Vrsanska 26e</span>
                  </p>
                  <p className="text-base text-gray-900">City Krk, 51500</p>
                  <p className="text-base text-gray-900">Croatia</p>
                </div>
              </div>
              <div className="flex-1">
                <h5 className="text-sm uppercase tracking-wider text-gray-400 mb-6">Quick Links</h5>
                <div className="space-y-5">
                  <button onClick={() => scrollToSection('home')} className="block text-base text-gray-700 hover:text-gray-900 hover:underline transition-colors cursor-pointer">Home</button>
                  <button onClick={() => scrollToSection('about')} className="block text-base text-gray-700 hover:text-gray-900 hover:underline transition-colors cursor-pointer">About</button>
                  <button onClick={() => scrollToSection('products')} className="block text-base text-gray-700 hover:text-gray-900 hover:underline transition-colors cursor-pointer">Products</button>
                  <button onClick={() => scrollToSection('contact')} className="block text-base text-gray-700 hover:text-gray-900 hover:underline transition-colors cursor-pointer">Contact</button>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">
              © 2025 MARIZO d. o. o. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900 transition-colors cursor-pointer">Website Builder</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
